import { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  createGuard,
  deleteGuard,
  fetchGuards,
  updateGuard,
} from "../../../redux/slices/guardSlice";
import { RootState, AppDispatch } from "../../../redux/store";
import { yupResolver } from "@hookform/resolvers/yup";
import { useForm } from "react-hook-form";
import { guardSchema } from "../../../constants/yup.schema";
import toast from "react-hot-toast";
import { useLocation } from "react-router-dom";

const useGuards = () => {
  const [isEdit, setIsEdit] = useState<boolean>(false);
  const [editingGuardId, setEditingGuardId] = useState<string>("");
  const location = useLocation();
  const { buildingId } = location.state || {};

  const dispatch: AppDispatch = useDispatch();

  // Modal state
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);

  const handleModal = () => {
    setIsModalOpen(!isModalOpen);
  };

  // Search state
  const [searchValue, setSearchValue] = useState<string>("");

  const {
    data: guards = [],
    loading,
    error: apiError,
  } = useSelector((state: RootState) => state.guards);

  // Fetch guards on load
  useEffect(() => {
    if (!loading) {
      dispatch(fetchGuards({ assignedBuilding: buildingId }));
    }
  }, [dispatch, buildingId]);

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchValue(e.target.value);
  };

  const filteredGuards = Array.isArray(guards?.data)
    ? guards.data.filter((guard: any) =>
        guard?.name.toLowerCase().includes(searchValue.toLowerCase())
      )
    : [];

  // Form configuration
  const defaultValues = {
    name: "",
    username: "",
    contactNumber: "",
    shiftStartTime: "",
    shiftEndTime: "",
    password: "",
  };

  const methods = useForm({
    defaultValues,
    resolver: yupResolver(guardSchema),
  });

  const {
    control,
    handleSubmit,
    formState: { errors, isSubmitting },
    reset,
  } = methods;

  // Submit handler
  const onSubmit = async (data: any) => {
    const newData = {
      ...data,
      shiftTiming: `${data.shiftFrom}-${data.shiftTo}`,
      assignedBuilding: buildingId,
    };
    console.log(newData);
    // return;
    try {
      if (isEdit && editingGuardId) {
        await dispatch(updateGuard({ id: editingGuardId, newData })).unwrap();
        toast.success("Guard updated successfully!");
        setIsEdit(false);
      } else {
        await dispatch(createGuard(newData)).unwrap();
        toast.success("Guard added successfully!");
      }
      handleModal(); // Close modal
      dispatch(fetchGuards({ assignedBuilding: buildingId }));
      reset(defaultValues); // Reset form
    } catch (err: any) {
      toast.error(err.message || "An error occurred");
    }
  };

  const handleDelete = async (item: any) => {
    try {
      await dispatch(deleteGuard(item._id)).unwrap();
      toast.success("Guard deleted successfully!");
      dispatch(fetchGuards({ assignedBuilding: buildingId }));
    } catch (err: any) {
      toast.error(err.message || "An error occurred");
    }
  };

  const handleEdit = async (item: any) => {
    console.log(item);
    setIsEdit(true);
    setEditingGuardId(item._id);
    handleModal();
    reset({
      name: item.name,
      username: item.username,
      contactNumber: item.contactNumber,
      shiftStartTime: item.shiftStartTime,
      shiftEndTime: item.shiftEndTime,
      password: item?.plainTextPassword || "",
    });
  };
  const actions = [
    {
      label: "Delete",

      handler: (row: any) => handleDelete(row),
    },
    {
      label: "Edit",
      handler: (row: any) => handleEdit(row),
    },
  ];
  return {
    isModalOpen,
    handleModal,
    searchValue,
    handleSearchChange,
    filteredGuards,
    guards,
    loading,
    methods,
    control,
    errors,
    handleSubmit,
    isSubmitting,
    onSubmit,
    actions,
    isEdit,
  };
};

export default useGuards;
